/**
 * iter295 — Plaques identiques chez plusieurs clients : ce n'est pas toujours un
 * doublon (véhicule revendu). L'admin tranche cas par cas et choisit la fiche à
 * conserver ; les autres sont absorbées dedans.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LIENS_LABELS = {
  or_collection: "OR",
  factures: "factures",
  devis_facturation: "devis",
  photos: "photos",
};

const resumeLiens = (liens) =>
  Object.entries(liens || {})
    .map(([k, n]) => `${n} ${LIENS_LABELS[k] || k}`)
    .join(" · ") || "aucun dossier";

const PlaquesMultiClientsCard = () => {
  const [groupes, setGroupes] = useState(null);
  const [choix, setChoix] = useState({});
  const [busy, setBusy] = useState(false);
  const [enCours, setEnCours] = useState(null);

  const charger = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/vehicules/plaques-multi-clients");
      setGroupes(data.groupes || []);
      setChoix(
        Object.fromEntries((data.groupes || []).map((g) => [g.plaque, g.suggere_garder])),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recherche impossible");
    } finally {
      setBusy(false);
    }
  };

  const fusionner = async (groupe) => {
    const gardeId = choix[groupe.plaque];
    const garde = groupe.vehicules.find((v) => v.id === gardeId);
    const autres = groupe.vehicules.filter((v) => v.id !== gardeId);
    if (!garde || autres.length === 0) return;
    if (
      !window.confirm(
        `Fusionner ${groupe.plaque} ?\n\nFiche conservée : ${garde.client_nom}`
        + `\nFiches absorbées : ${autres.map((v) => v.client_nom).join(", ")}`
        + "\n\nTous les dossiers, factures, devis et photos seront rattachés à la fiche"
        + " conservée. Cette action est définitive.",
      )
    ) return;
    setEnCours(groupe.plaque);
    try {
      const { data } = await api.post("/admin/vehicules/fusionner", {
        garde_id: gardeId,
        supprime_ids: autres.map((v) => v.id),
      });
      toast.success(
        `${groupe.plaque} fusionnée · ${data.references_deplacees} rattachement(s) déplacé(s)`,
      );
      setGroupes((prev) => prev.filter((g) => g.plaque !== groupe.plaque));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fusion impossible");
    } finally {
      setEnCours(null);
    }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="plaques-multi-clients-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-amber-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Plaques partagées entre deux clients
        </h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Ces plaques existent chez plusieurs clients : véhicule revendu, ou simple doublon de
        saisie. Le nettoyage automatique n&apos;y touche jamais. Choisis ici la fiche à conserver,
        les autres seront absorbées dedans avec leurs dossiers.
      </p>

      <Button
        variant="outline" size="sm" disabled={busy}
        onClick={charger}
        data-testid="plaques-multi-charger"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
        {groupes === null ? "Chercher les plaques partagées" : "Actualiser"}
      </Button>

      {groupes !== null && groupes.length === 0 && (
        <div
          className="mt-3 flex items-center gap-1.5 text-xs text-gray-600"
          data-testid="plaques-multi-vide"
        >
          <Check className="w-3.5 h-3.5 text-[#52a675]" />
          Aucune plaque partagée entre plusieurs clients.
        </div>
      )}

      {(groupes || []).map((g) => (
        <div
          key={g.plaque}
          className="mt-3 rounded-lg border border-gray-200 p-3"
          data-testid={`plaques-multi-groupe-${g.plaque}`}
        >
          <div className="font-mono text-sm font-semibold text-gray-900">{g.plaque}</div>
          <div className="mt-2 space-y-1.5">
            {g.vehicules.map((v) => (
              <label
                key={v.id}
                className={`flex items-start gap-2 p-2 rounded cursor-pointer border text-xs transition-colors ${
                  choix[g.plaque] === v.id
                    ? "border-[#52a675] bg-[#52a675]/5"
                    : "border-transparent hover:bg-gray-50"
                }`}
                data-testid={`plaques-multi-choix-${v.id}`}
              >
                <input
                  type="radio"
                  className="mt-0.5 accent-[#52a675]"
                  name={`garde-${g.plaque}`}
                  checked={choix[g.plaque] === v.id}
                  onChange={() => setChoix((p) => ({ ...p, [g.plaque]: v.id }))}
                />
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{v.client_nom}</div>
                  <div className="text-gray-500">
                    {[v.marque, v.modele].filter(Boolean).join(" ") || "Modèle inconnu"}
                    {v.vin ? ` · VIN ${v.vin}` : ""}
                  </div>
                  <div className="text-gray-500">
                    {v.km ? `${v.km.toLocaleString("fr-CH")} km` : "km inconnu"}
                    {v.derniere_activite ? ` · maj ${v.derniere_activite.slice(0, 10)}` : ""}
                    {` · ${resumeLiens(v.liens)}`}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <Button
            size="sm" className="mt-2 bg-[#52a675] hover:bg-[#3d8a5c]"
            disabled={enCours === g.plaque}
            onClick={() => fusionner(g)}
            data-testid={`plaques-multi-fusionner-${g.plaque}`}
          >
            {enCours === g.plaque ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Fusionner sur la fiche choisie
          </Button>
        </div>
      ))}
    </Card>
  );
};

export default PlaquesMultiClientsCard;
